// Third-party
import React from 'react';
import { connect } from 'react-redux';

// Namespaces
import * as ProposalNS from '../../../../modules/proposal/Proposal';

// Utils
import { FormattedMessage, formatMessageWithLocale, formatLocalizedString } from '../../../../utils/i18n';
import { enumerateJSX, enumerateArray } from '../../../../utils/objects';
import { COMPARISON_ITEM_TYPES } from '../../../../utils/constants';

// Components
import { HoldingIcon } from '../../common';

// Redux
import { ReduxState } from '../../../../redux/modules/types';
import Dictionary from '@/types/dictionary';
import ComparisonItem from '@/types/comparison-item';
import Locale from '@/types/locale';

type StateProps = {
  comparisonItems: ComparisonItem[];
};

type Props = StateProps & {
  items: ComparisonItem[];
  locale?: Locale;
  proposal?: ProposalNS.Proposal;
};

/**
 * IMPORTANT NOTE:
 *
 * This disclaimer is only used for individual funds at the moment, since it's only
 * used in funds-only proposal templates. If this needs to be extended, we'll have to
 * revisit the disclaimer logic.
 */
const CalculationBenchmarksDisclaimer = ({ items, locale, proposal, comparisonItems }: Props) => {
  const funds = items.filter(item => item.type === COMPARISON_ITEM_TYPES.FUND);

  if (funds.length === 0) {
    return null;
  }

  type GroupedDisclaimer = {
    items: Array<{
      type: string;
      symbol: string;
      color?: string;
    }>;
    benchmarks: string;
  };

  const selectedBenchmark = proposal && proposal.selectedBenchmarkId && comparisonItems.find(item => item._id === proposal.selectedBenchmarkId);
  let groupedDisclaimers;

  if (selectedBenchmark) {
    // If we do have a benchmark selected for calculations, then all funds will be using that benchmark.
    // In that case, group them all under the same disclaimer.
    groupedDisclaimers = {
      [selectedBenchmark._id]: {
        items: funds.map(fund => ({
          type: fund.type,
          color: fund.color,
          symbol: fund.holdings.length > 0 && fund.holdings[0].symbol ? fund.holdings[0].symbol : '',
        })),
        benchmarks: formatLocalizedString(selectedBenchmark.name, locale),
      },
    };
  } else {
    groupedDisclaimers = funds.reduce((acc, fund) => {
      if (!fund.benchmarks) {
        return acc;
      }

      // Group by market benchmark and risk-free benchmark. A disclaimer will be shown for each of the combinations.
      // (Same market benchmark but different risk-free = 2 disclaimers, same for both = 1)
      const market = fund.benchmarks.market && fund.benchmarks.market[fund._id];
      const riskFree = fund.benchmarks.riskFree && fund.benchmarks.riskFree[fund._id];

      const marketName = market ? formatLocalizedString(market.names.full, locale) : null;
      const riskFreeName = riskFree ? formatLocalizedString(riskFree.names.full, locale) : null;
      const benchmarksStr = enumerateArray(
        [marketName, riskFreeName].filter(name => name != null),
        formatMessageWithLocale('Common.And', locale),
      );

      const id = `${market ? market.lipperId : ''}-${riskFree ? riskFree.lipperId : ''}`;
      const symbol = fund.holdings.length > 0 && fund.holdings[0].symbol ? fund.holdings[0].symbol : '';

      if (!symbol) {
        return acc;
      }

      const newItem = {
        symbol,
        type: fund.type,
        color: fund.color,
      };

      if (acc[id]) {
        acc[id].items.push(newItem);
      } else {
        acc[id] = {
          items: [
            {
              symbol,
              type: fund.type,
              color: fund.color,
            },
          ],
          benchmarks: benchmarksStr,
        };
      }

      return acc;
    }, {} as Dictionary<GroupedDisclaimer>);
  }

  return (
    <div className="disclaimer">
      {Object.values(groupedDisclaimers).map(disclaimer => (
        <span key={disclaimer.benchmarks}>
          <FormattedMessage locale={locale} id="Disclaimers.CalculationBenchmarks.1" />

          {enumerateJSX(
            disclaimer.items.map(item => (
              <span key={item.symbol} className="item-name" data-private data-dd-privacy="mask">
                <HoldingIcon type={item.type} color={item.color} />
                {item.symbol}
              </span>
            )),
            formatMessageWithLocale('Common.And', locale),
          )}

          <FormattedMessage locale={locale} id="Disclaimers.CalculationBenchmarks.2" values={{ benchmarks: disclaimer.benchmarks }} />
        </span>
      ))}
    </div>
  );
};

const mapStateToProps = (state: ReduxState): StateProps => {
  const currentComparison = state.comparison.activeComparison;

  return {
    comparisonItems: currentComparison ? currentComparison.items : [],
  };
};

export default connect(mapStateToProps)(CalculationBenchmarksDisclaimer);
